"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";

const EditProjectConfirmQuery = graphql(`
  query EditProjectConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorkerProject { id name color description }
      ... on SearchAnalyticsProject { id name color description }
      ... on UserAnalyticsProject { id name color description }
    }
  }
`);

type EditProjectInput = {
  projectId: string;
  name?: string;
  color?: string;
  description?: string;
};

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function EditProjectConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const isEditTool = isToolUIPart(part) && getToolName(part) === "edit_project";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<EditProjectInput>;
  const projectId = input.projectId ?? "";

  const [{ data, fetching }] = useQuery({
    query: EditProjectConfirmQuery,
    variables: { id: projectId },
    pause: !isEditTool || !isInputAvailable || !projectId,
  });

  if (!isEditTool) return null;
  if (!isInputAvailable) return null;

  const node = data?.node;
  const project =
    node?.__typename === "IndexWorkerProject" ||
    node?.__typename === "SearchAnalyticsProject" ||
    node?.__typename === "UserAnalyticsProject"
      ? node
      : null;

  const pending = fetching || !project; // ensure skeleton shows until data is present

  const oldName = project?.name ?? "";
  const oldColor = project?.color ?? "#999999";
  const oldDesc =
    typeof project?.description === "string" ? project?.description : "";

  const newName = typeof input.name === "string" ? input.name : undefined;
  const newColor = typeof input.color === "string" ? input.color : undefined;
  const newDesc =
    typeof input.description === "string" ? input.description : undefined;

  const nameChanged =
    !!newName &&
    newName.trim() !== "" &&
    newName.trim() !== (oldName ?? "").trim();
  const normalizeHex = (v?: string) => (v ? v.trim().toLowerCase() : v);
  const colorChanged =
    !!newColor && normalizeHex(newColor) !== normalizeHex(oldColor);
  const descChanged = typeof newDesc === "string" && newDesc !== oldDesc;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to update this project?</Trans>
      </div>

      <ul className="mb-4 list-disc pl-5 space-y-2">
        <li>
          <Trans>Project Name: </Trans>
          {pending ? (
            <Skeleton className="h-4 w-24 inline-block align-middle bg-gray-400 dark:bg-gray-600" />
          ) : (
            <>
              <span>{oldName || "-"}</span>
              {nameChanged ? (
                <span className="ml-2 font-semibold">→ {newName}</span>
              ) : (
                <span className="ml-2">
                  (<Trans>No changes</Trans>)
                </span>
              )}
            </>
          )}
        </li>

        <li>
          <div className="flex items-center gap-2">
            <span>
              <Trans>Color: </Trans>
            </span>
            {pending ? (
              <Skeleton className="h-4 w-16 inline-block bg-gray-400 dark:bg-gray-600" />
            ) : (
              <>
                <span
                  aria-label="old color"
                  className="inline-block size-4 rounded border"
                  style={{ backgroundColor: oldColor }}
                />
                <span className="font-mono text-xs">{oldColor}</span>
                {colorChanged ? (
                  <>
                    <span className="mx-1">→</span>
                    <span
                      aria-label="new color"
                      className="inline-block size-4 rounded border"
                      style={{ backgroundColor: newColor }}
                    />
                    <span className="font-mono text-xs font-semibold">
                      {newColor}
                    </span>
                  </>
                ) : (
                  <span className="ml-2">
                    (<Trans>No changes</Trans>)
                  </span>
                )}
              </>
            )}
          </div>
        </li>

        <li>
          <div className="mb-1">
            <Trans>Description:</Trans>
            {!pending && !descChanged ? (
              <span className="ml-2">
                (<Trans>No changes</Trans>)
              </span>
            ) : null}
          </div>
          {pending ? (
            <Skeleton className="h-10 w-full max-w-md bg-gray-400 dark:bg-gray-600" />
          ) : descChanged ? (
            <div className="rounded border bg-muted/30 p-2 text-xs">
              <div>
                <span className="mr-2">
                  <Trans>Current:</Trans>
                </span>
                <span>{oldDesc || "-"}</span>
              </div>
              <div className="mt-1">
                <span className="mr-2">
                  <Trans>New:</Trans>
                </span>
                <span className="font-semibold">{newDesc || "-"}</span>
              </div>
            </div>
          ) : null}
        </li>
      </ul>

      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
